import React from 'react';

/**
 * WeMovedPage - "We've Moved" landing page for Savvy.coop redirect
 * Clean, warm design with animated entrance
 */
export const WeMovedPage = () => {
  return (
    <div style={styles.container}>
      {/* Animated background blobs */}
      <div style={styles.blobContainer}>
        <div style={{ ...styles.blob, ...styles.blob1 }} />
        <div style={{ ...styles.blob, ...styles.blob2 }} />
        <div style={{ ...styles.blob, ...styles.blob3 }} />
      </div>

      {/* Main content with gradient glow */}
      <div style={styles.contentWrapper}>
        <div style={styles.contentGlow} />
        <div style={styles.content}>
        {/* Logo Transition: Savvy → Real Patients */}
        <div style={styles.logoTransition}>
          {/* Savvy Logo (actual image) */}
          <img
            src="/savvy-logo.png"
            alt="Savvy Cooperative"
            style={styles.savvyLogoImg}
          />

          {/* Animated Arrow */}
          <div style={styles.arrow} className="arrow-pulse">
            <svg width="48" height="32" viewBox="0 0 48 32" fill="none" style={{ display: 'block' }}>
              <path d="M32 6L44 16L32 26" stroke="#272726" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 16H44" stroke="#272726" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>

          {/* Real Patients Logo */}
          <div style={styles.realPatientsLogo}>
            <span style={styles.logoReal}>Real</span>
            <span style={styles.logoPatientsWrapper}>
              <span style={styles.logoHighlight} />
              <span style={styles.logoPatients}>Patients</span>
              <span style={styles.trademark}>™</span>
            </span>
          </div>
        </div>

        {/* Headline */}
        <h1 style={styles.headline}>
          We've Moved!
        </h1>

        {/* Body copy */}
        <div style={styles.body}>
          <p style={styles.bodyMain}>
            After nearly a decade of connecting patient voices with the companies<br />
            shaping healthcare, we've grown into something bigger.
          </p>

          {/* Tagline */}
          <p style={styles.tagline}>
            New name. Same heart. Bigger impact.
          </p>
        </div>

        {/* CTA Button */}
        <a
          href="https://realpatients.com"
          style={styles.ctaButton}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a39'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#272726'}
        >
          Visit Real Patients →
        </a>

        {/* Contact */}
        <p style={styles.contact}>
          Questions? <a href="mailto:hello@realpatients.com" style={styles.contactLink}>hello@realpatients.com</a>
        </p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        /* Floating background blobs */
        @keyframes floatBlob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 10px) scale(0.95); }
        }

        @keyframes floatBlob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-25px, 15px) scale(0.95); }
          66% { transform: translate(20px, -25px) scale(1.05); }
        }

        @keyframes floatBlob3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(15px, 20px) scale(1.03); }
        }

        /* Arrow pulse animation */
        @keyframes arrowPulse {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(6px); }
        }

        .arrow-pulse {
          animation: arrowPulse 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f7f5f3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#272726',
    position: 'relative',
    overflow: 'hidden',
    padding: '40px 24px',
  },

  // Background blobs
  blobContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  blob: {
    position: 'absolute',
    borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
    filter: 'blur(40px)',
    opacity: 0.6,
  },
  blob1: {
    width: '400px',
    height: '400px',
    background: 'rgba(235, 160, 155, 0.4)',
    top: '-100px',
    right: '-100px',
    animation: 'floatBlob1 20s ease-in-out infinite',
  },
  blob2: {
    width: '350px',
    height: '350px',
    background: 'rgba(245, 185, 165, 0.35)',
    bottom: '-80px',
    left: '-80px',
    animation: 'floatBlob2 25s ease-in-out infinite',
  },
  blob3: {
    width: '250px',
    height: '250px',
    background: 'rgba(250, 200, 160, 0.3)',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    animation: 'floatBlob3 18s ease-in-out infinite',
  },

  // Content wrapper with glow
  contentWrapper: {
    position: 'relative',
    zIndex: 1,
  },
  contentGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '120%',
    height: '120%',
    background: 'radial-gradient(ellipse at center, rgba(245, 185, 165, 0.25) 0%, rgba(235, 160, 155, 0.15) 30%, transparent 70%)',
    borderRadius: '50%',
    filter: 'blur(40px)',
    pointerEvents: 'none',
  },

  // Content
  content: {
    position: 'relative',
    textAlign: 'center',
    maxWidth: '700px',
  },

  // Logo Transition
  logoTransition: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
    marginBottom: '48px',
    flexWrap: 'wrap',
  },

  // Savvy Logo (image)
  savvyLogoImg: {
    height: '80px',
    width: 'auto',
    objectFit: 'contain',
  },

  // Arrow
  arrow: {
    padding: '0 8px',
  },

  // Real Patients Logo
  realPatientsLogo: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: '4px',
  },
  logoReal: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '28px',
    fontWeight: 700,
    color: '#272726',
  },
  logoPatientsWrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  logoHighlight: {
    position: 'absolute',
    bottom: '2px',
    left: '-4px',
    right: '-4px',
    height: '40%',
    background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.85) 0%, rgba(245, 185, 165, 0.8) 40%, rgba(250, 200, 160, 0.75) 70%, rgba(245, 215, 170, 0.6) 100%)',
    borderRadius: '2px 8px 6px 2px',
    transform: 'skewX(-2deg) rotate(-1deg)',
    zIndex: -1,
  },
  logoPatients: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '28px',
    fontWeight: 700,
    color: '#272726',
    position: 'relative',
    zIndex: 1,
  },
  trademark: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    verticalAlign: 'super',
    marginLeft: '1px',
  },

  // Headline
  headline: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: 'clamp(36px, 8vw, 56px)',
    fontWeight: 700,
    marginBottom: '32px',
    lineHeight: 1.1,
  },

  // Body
  body: {
    marginBottom: '40px',
  },
  bodyMain: {
    fontSize: '18px',
    lineHeight: 1.6,
    marginBottom: '24px',
    color: '#272726',
  },
  tagline: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#272726',
  },

  // CTA Button
  ctaButton: {
    display: 'inline-block',
    backgroundColor: '#272726',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    padding: '16px 32px',
    borderRadius: '6px',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    marginBottom: '32px',
  },

  // Contact
  contact: {
    fontSize: '14px',
    color: '#666',
  },
  contactLink: {
    color: '#272726',
    textDecoration: 'underline',
  },
};

export default WeMovedPage;
